import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/authContext';
import { useEstadoApp } from '../context/EstadoAppContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { initiateSocketConnection } from '../service/socketio';

const AREAS = [
	{ value: 'PROYECTOS', label: 'Obras' },
	{ value: 'MEJORAS', label: 'Mejoras' },
	{ value: 'MICROCREDITOS', label: 'Microcréditos' },
];

const TIPOS_PAGO = [
	{ value: 'anticipo', label: 'Anticipo' },
	{ value: '2do_pago', label: '2do Pago' },
	{ value: '3er_pago', label: '3er Pago' },
	{ value: 'total', label: 'Total' },
	{ value: 'adenda', label: 'Adenda' },
];

const TIPOS_OBJETO = [
	{ value: 'Proyecto', label: 'Proyecto' },
	{ value: 'Adenda', label: 'Adenda' },
	// Valor derivado (no existe en la BD): son las adendas que son la 2ª de su obra.
	// El backend lo resuelve, y lo acota a tipo_objeto = 'Adenda' para que sea un
	// subconjunto de la opción "Adenda" de arriba.
	{ value: 'Adenda 2', label: 'Adenda 2' },
];

const PAGE_SIZE = 50;

const ANIOS_ANTICIPO = ['2016','2017','2018','2019','2020','2021','2022','2023','2024','2025','2026','2027'];

const fmtArea = (val) => {
	if (val === 'PROYECTOS') return 'OBRAS';
	return val || '-';
};

const fmtTipoObjeto = (val) => {
	if (val === 'PROYECTO') return 'OBRAS';
	return val || '-';
};

const fmtMonto = (val) => {
	if (val === null || val === undefined) return '-';
	return '$' + Number(val).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtPct = (val) => (val === null || val === undefined ? '-' : Number(val).toLocaleString('es-AR', { maximumFractionDigits: 2 }) + '%');

const fmtFecha = (val) => {
	if (!val) return '-';
	const s = String(val);
	// Si viene ISO con hora (ej: 2026-07-01T00:00:00.000Z) o datetime, quedarse con la fecha
	const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
	if (match) return match[1];
	return s;
};

const ESTADOS_VERIF = [
	{ value: 'ok', label: 'OK' },
	{ value: 'revisar', label: 'Revisar' },
	{ value: 'pendiente', label: 'Pendiente' },
];

const estadoVerifLabel = (val) => {
	if (!val) return 'Sin verificar';
	const found = ESTADOS_VERIF.find((e) => e.value === val);
	return found ? found.label : val;
};

const estadoVerifCls = (val) => {
	switch (val) {
		case 'ok': return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
		case 'revisar': return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
		case 'pendiente': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
		default: return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
	}
};

export default function AnalisisPagosPage() {
	const { role, token, user } = useContext(AuthContext);
	const navigate = useNavigate();
	const location = useLocation();
	const { cambiarEstadoApp } = useEstadoApp();
	const { setItem } = useLocalStorage();
	const [partidos, setPartidos] = useState([]);
	const [estados, setEstados] = useState([]);
	const [arrayEditando, setArrayEditando] = useState([]);
	const [socket, setSocket] = useState(null);
	const [filters, setFilters] = useState({ partido: 'Todos', area: 'Todos', tipo_objeto: 'Todos', tipo_pago: 'Todos', estado_verif: 'Todos', estado_obra: 'Todos', year_anticipo: 'Todos', search: '', soloCoincidentes: false, soloMontosDistintos: false });
	const [data, setData] = useState({ rows: [], total: 0, page: 1, pageSize: PAGE_SIZE });
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [showRendicion, setShowRendicion] = useState(false);
	const [verifModal, setVerifModal] = useState(null); // pago en edición de verificación (o null)
	const [verifEstado, setVerifEstado] = useState('pendiente');
	const [verifObservacion, setVerifObservacion] = useState('');
	const [verifGuardando, setVerifGuardando] = useState(false);
	const debounceRef = useRef(null);
	// Restauración del orden al volver de editar (doble clic → InicioPage → guardar):
	// se guarda un snapshot (filtros + página + ids ordenados) y al regresar se
	// mantiene el orden exacto aunque la obra editada haya cambiado su fecha.
	const [modoRestaurado, setModoRestaurado] = useState(false);
	const [ordenFijo, setOrdenFijo] = useState(null);
	const [paginaFija, setPaginaFija] = useState(1);
	const fetchSeqRef = useRef(0);

	// Verifica si OTRO usuario (distinto al actual) está editando la obra (status 'editando')
	const isEditingByOtherUser = (idPartidoObra) => {
		const safeArray = Array.isArray(arrayEditando) ? arrayEditando : [];
		return safeArray.some((objeto) => {
			if (!objeto || !objeto.id_edit || !objeto.user_edit || !objeto.status) return false;
			const userEditClean = objeto.user_edit.replace(/"/g, '');
			return objeto.id_edit === idPartidoObra && objeto.status === 'editando' && userEditClean !== user;
		});
	};

	// Inicializar conexión Socket.IO (una sola vez)
	useEffect(() => {
		const s = initiateSocketConnection();
		setSocket(s);
		const onConnect = () => console.log('[analisis-pagos socket] connected', s.id);
		const onDisconnect = () => console.log('[analisis-pagos socket] disconnected');
		s.on('connect', onConnect);
		s.on('disconnect', onDisconnect);
		return () => {
			s.off('connect', onConnect);
			s.off('disconnect', onDisconnect);
			try { s.close(); } catch (e) {}
		};
	}, []);

	// Escuchar cambios del array de ediciones (para marcar filas "en edición" por otro usuario)
	useEffect(() => {
		if (!socket) return;
		const consultarArrayEditando = async () => {
			try {
				const res = await fetch('/api/arrayEditando', {
					headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': '*' },
				});
				const result = await res.json();
				setArrayEditando(Array.isArray(result) ? result : []);
			} catch (e) {
				setArrayEditando([]);
			}
		};
		const procesarArray = (data) => setArrayEditando(Array.isArray(data) ? data : []);
		socket.on('array_editando', procesarArray);
		consultarArrayEditando();
		return () => {
			socket.off('array_editando', procesarArray);
		};
	}, [socket]);

	// Doble click en una fila → abrir la edición de la obra (patrón TablaObras/TablaProyectos)
	const handleRowDoubleClick = (idPartidoObra) => {
		console.log('Navegando a InicioPage con id_partido_obra:', idPartidoObra);
		// Guardar snapshot para mantener filtros, página y orden al volver de editar.
		try {
			localStorage.setItem('analisisPagosSnapshot', JSON.stringify({
				filters,
				page: data.page,
				orderIds: data.rows.map(r => r.id),
			}));
		} catch (e) { /* si falla el snapshot, no bloquea la navegación */ }
		setItem('idPartidoObra', idPartidoObra);
		// Si OTRO usuario está editando este registro entramos en modo SOLO LECTURA (isEditingPagos="true").
		// Si nadie más lo edita, entramos en modo edición normal (isEditingPagos="false"),
		// para que InicioPage levante los datos correctamente.
		const otroEdita = isEditingByOtherUser(idPartidoObra);
		setItem('isEditingPagos', otroEdita ? true : false);
		cambiarEstadoApp('edit_desde_pagos', {
			obra: idPartidoObra,
			origen: 'analisis_pagos',
		});
		navigate(`/inicio?id_partido_obra=${encodeURIComponent(idPartidoObra)}`);
	};

	// Cargar lista de partidos disponibles (para el filtro)
	useEffect(() => {
		if (role !== 'admin') return;
		const fetchPartidos = async () => {
			try {
				const res = await fetch('/api/analisis-pagos/partidos', {
					headers: {
						'Content-Type': 'application/json',
						...(token ? { Authorization: `Bearer ${token}` } : {}),
					},
				});
				if (res.ok) {
					const rows = await res.json();
					setPartidos(rows.map((r) => ({ value: r.partido_nombre, label: r.partido_nombre })));
				}
			} catch (e) {
				// Si falla, no bloquea la página
			}
		};
		fetchPartidos();
	}, [role, token]);

	// Cargar lista de estados de obra disponibles (para el filtro)
	useEffect(() => {
		if (role !== 'admin') return;
		const fetchEstados = async () => {
			try {
				const res = await fetch('/api/analisis-pagos/estados', {
					headers: {
						'Content-Type': 'application/json',
						...(token ? { Authorization: `Bearer ${token}` } : {}),
					},
				});
				if (res.ok) {
					const rows = await res.json();
					setEstados(rows.map((r) => ({ value: r.estado, label: r.estado, obras: r.obras })));
				}
			} catch (e) {
				// Si falla, no bloquea la página
			}
		};
		fetchEstados();
	}, [role, token]);

	const loadData = useCallback(async (page = 1, opts = {}) => {
		const seq = ++fetchSeqRef.current;
		setLoading(true);
		setError('');
		try {
			const { fijoIds = null, filtros = filters } = opts;
			const params = new URLSearchParams();
			if (filtros.partido && filtros.partido !== 'Todos') params.append('partido', filtros.partido);
			if (filtros.area && filtros.area !== 'Todos') params.append('area', filtros.area);
			if (filtros.tipo_pago && filtros.tipo_pago !== 'Todos') params.append('tipo_pago', filtros.tipo_pago);
			if (filtros.tipo_objeto && filtros.tipo_objeto !== 'Todos') params.append('tipo_objeto', filtros.tipo_objeto);
			if (filtros.estado_verif && filtros.estado_verif !== 'Todos') params.append('estado_verif', filtros.estado_verif);
			if (filtros.estado_obra && filtros.estado_obra !== 'Todos') params.append('estado_obra', filtros.estado_obra);
			if (filtros.year_anticipo && filtros.year_anticipo !== 'Todos') params.append('year_anticipo', filtros.year_anticipo);
			if (filtros.search && filtros.search.trim() !== '') params.append('search', filtros.search.trim());
			if (filtros.soloCoincidentes) params.append('soloCoincidentes', 'true');
			if (filtros.soloMontosDistintos) params.append('soloMontosDistintos', 'true');
			if (Array.isArray(fijoIds) && fijoIds.length) params.append('ordenFijo', fijoIds.join(','));
			params.append('page', page);
			params.append('pageSize', PAGE_SIZE);

			const res = await fetch(`/api/analisis-pagos?${params.toString()}`, {
				headers: {
					'Content-Type': 'application/json',
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
			});
			if (!res.ok) throw new Error('Error al cargar los pagos');
			const result = await res.json();
			if (seq !== fetchSeqRef.current) return; // respuesta vieja, se descarta
			setData(result);
		} catch (err) {
			if (seq === fetchSeqRef.current) setError(err.message);
		} finally {
			if (seq === fetchSeqRef.current) setLoading(false);
		}
	}, [filters, token]);

	useEffect(() => {
		if (role !== 'admin') return;
		if (modoRestaurado && ordenFijo) {
			// Al volver de editar: conservar filtros, página y orden exactos del snapshot.
			loadData(paginaFija, { fijoIds: ordenFijo });
			return;
		}
		loadData(1);
	}, [role, token, filters, loadData, modoRestaurado, ordenFijo, paginaFija]);

	// Al volver de editar (InicioPage navega a /analisis-pagos?edicionExitosa=true):
	// restaurar el snapshot guardado en el doble clic para mantener el orden.
	useEffect(() => {
		const query = new URLSearchParams(location.search);
		if (!query.get('edicionExitosa')) return;

		let snap = null;
		try {
			snap = JSON.parse(localStorage.getItem('analisisPagosSnapshot') || 'null');
		} catch (e) { snap = null; }

		if (snap && Array.isArray(snap.orderIds) && snap.orderIds.length && snap.filters) {
			// El efecto de datos vuelve a cargar con ordenFijo al cambiar filters/modoRestaurado.
			setOrdenFijo(snap.orderIds);
			setPaginaFija(snap.page || 1);
			setModoRestaurado(true);
			setFilters((prev) => ({ ...prev, ...snap.filters }));
		}

		localStorage.removeItem('analisisPagosSnapshot');
		const newSearch = new URLSearchParams(location.search);
		newSearch.delete('edicionExitosa');
		window.history.replaceState({}, '', [location.pathname, newSearch.toString()].filter(Boolean).join('?'));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleFilterChange = (name, value) => {
		setModoRestaurado(false);
		setOrdenFijo(null);
		setFilters((prev) => ({ ...prev, [name]: value }));
	};

	const handleSearchChange = (value) => {
		setModoRestaurado(false);
		setOrdenFijo(null);
		setFilters((prev) => ({ ...prev, search: value }));
	};

	const resetFilters = () => {
		setModoRestaurado(false);
		setOrdenFijo(null);
		setFilters({ partido: 'Todos', area: 'Todos', tipo_objeto: 'Todos', tipo_pago: 'Todos', estado_verif: 'Todos', estado_obra: 'Todos', year_anticipo: 'Todos', search: '', soloCoincidentes: false, soloMontosDistintos: false });
	};

	const navegarPagina = (n) => {
		setModoRestaurado(false);
		setOrdenFijo(null);
		loadData(n);
	};

	// Abrir el modal de verificación para un pago
	const abrirVerifModal = (r) => {
		setVerifModal(r);
		setVerifEstado(r.estado_verif || 'pendiente');
		setVerifObservacion(r.observacion_verif || '');
	};

	const cerrarVerifModal = () => {
		if (verifGuardando) return;
		setVerifModal(null);
	};

	// Guardar el estado de verificación (PUT /api/analisis-pagos/:id/verif)
	const guardarVerif = async () => {
		if (!verifModal || verifGuardando) return;
		setVerifGuardando(true);
		try {
			const res = await fetch(`/api/analisis-pagos/${verifModal.id}/verif`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify({ estadoVerif: verifEstado, observacionVerif: verifObservacion }),
			});
			if (!res.ok) {
				const err = await res.json().catch(() => ({}));
				throw new Error(err.error || 'Error al guardar la verificación');
			}
			// Actualizar la fila localmente y cerrar el modal
			setData((prev) => ({
				...prev,
				rows: prev.rows.map((row) =>
					row.id === verifModal.id
						? { ...row, estado_verif: verifEstado, observacion_verif: verifObservacion }
						: row
				),
			}));
			setVerifModal(null);
		} catch (err) {
			setError(err.message);
		} finally {
			setVerifGuardando(false);
		}
	};

	const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

	if (role !== 'admin') {
		return (
			<div className="p-8 text-center text-gray-500">
				Acceso denegado. Solo administradores.
			</div>
		);
	}

	return (
		<div className="p-6 w-full max-w-full">
			<h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">
				Análisis de Pagos
			</h1>

			{/* Filtros */}
			<div className="mb-4 rounded-xl border border-gray-200 bg-white dark:bg-gray-800 shadow-sm p-4 border-b border-gray-100 dark:border-gray-700">
				{/* Fila 1: filtros de selección */}
				<div className="flex flex-wrap gap-4">
					<div className="flex-1 min-w-[160px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Partido</label>
						<select
							value={filters.partido}
							onChange={(e) => handleFilterChange('partido', e.target.value)}
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						>
							<option value="Todos">Todos los partidos</option>
							{partidos.map((p) => (
								<option key={p.value} value={p.value}>{p.label}</option>
							))}
						</select>
					</div>

					<div className="flex-1 min-w-[140px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Área</label>
						<select
							value={filters.area}
							onChange={(e) => handleFilterChange('area', e.target.value)}
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						>
							<option value="Todos">Todas las áreas</option>
							{AREAS.map((a) => (
								<option key={a.value} value={a.value}>{a.label}</option>
							))}
						</select>
					</div>

					<div className="flex-1 min-w-[140px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Objeto Exp</label>
						<select
							value={filters.tipo_objeto}
							onChange={(e) => handleFilterChange('tipo_objeto', e.target.value)}
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						>
							<option value="Todos">Todos los objetos</option>
							{TIPOS_OBJETO.map((t) => (
								<option key={t.value} value={t.value}>{t.label}</option>
							))}
						</select>
					</div>

					<div className="flex-1 min-w-[140px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Tipo de Pago</label>
						<select
							value={filters.tipo_pago}
							onChange={(e) => handleFilterChange('tipo_pago', e.target.value)}
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						>
							<option value="Todos">Todos los tipos</option>
							{TIPOS_PAGO.map((t) => (
								<option key={t.value} value={t.value}>{t.label}</option>
							))}
						</select>
					</div>

					<div className="flex-1 min-w-[130px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Año (anticipo o total)</label>
						<select
							value={filters.year_anticipo}
							onChange={(e) => handleFilterChange('year_anticipo', e.target.value)}
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						>
							<option value="Todos">Todos los años</option>
							{ANIOS_ANTICIPO.map((y) => (
								<option key={y} value={y}>{y}</option>
							))}
						</select>
					</div>

					<div className="flex-1 min-w-[150px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Estado de Verif.</label>
						<select
							value={filters.estado_verif}
							onChange={(e) => handleFilterChange('estado_verif', e.target.value)}
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						>
							<option value="Todos">Todos los estados</option>
							<option value="sin_verificar">Sin verificar</option>
							{ESTADOS_VERIF.map((e) => (
								<option key={e.value} value={e.value}>{e.label}</option>
							))}
						</select>
					</div>

					<div className="flex-1 min-w-[150px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Estado de Obra</label>
						<select
							value={filters.estado_obra}
							onChange={(e) => handleFilterChange('estado_obra', e.target.value)}
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						>
							<option value="Todos">Todos los estados</option>
							{estados.map((e) => (
								<option key={e.value} value={e.value}>{e.label}</option>
							))}
						</select>
					</div>
				</div>

				{/* Fila 2: busqueda + opciones */}
				<div className="mt-3 flex flex-wrap items-center gap-4">
					<div className="min-w-[260px] flex-1">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Buscar (id / expediente)</label>
						<input
							type="text"
							value={filters.search}
							onChange={(e) => handleSearchChange(e.target.value)}
							placeholder="Ej: 492 o EX-2020-..."
							className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						/>
					</div>
					<div className="flex items-center gap-6">
						<label className="flex items-center gap-2 cursor-pointer select-none">
							<input
								type="checkbox"
								checked={filters.soloCoincidentes}
								onChange={(e) => handleFilterChange('soloCoincidentes', e.target.checked)}
								className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
							/>
							<span className="text-sm text-gray-700 dark:text-gray-300">
								Coincidencia habitat
							</span>
						</label>
						<label className="flex items-center gap-2 cursor-pointer select-none">
							<input
								type="checkbox"
								checked={filters.soloMontosDistintos}
								onChange={(e) => handleFilterChange('soloMontosDistintos', e.target.checked)}
								className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
							/>
							<span className="text-sm text-gray-700 dark:text-gray-300">
								Solo montos distintos
							</span>
						</label>
						<label className="flex items-center gap-2 cursor-pointer select-none">
							<input
								type="checkbox"
								checked={showRendicion}
								onChange={(e) => setShowRendicion(e.target.checked)}
								className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
							/>
							<span className="text-sm text-gray-700 dark:text-gray-300">
								Exp. de rendición
							</span>
						</label>
					</div>
					<div className="flex items-center gap-4">
						<button
							onClick={resetFilters}
							className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
						>
							Resetear filtros
						</button>
						<span className="text-xs text-gray-400">
							{data.total.toLocaleString('es-AR')} registros
						</span>
					</div>
				</div>
			</div>

			{loading && <p className="text-gray-500 mb-4">Cargando datos...</p>}
			{error && <p className="text-red-600 mb-4">{error}</p>}

			{/* Tabla */}
			<div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-x-auto">
				<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
					<thead className="bg-gray-50 dark:bg-gray-700">
						<tr>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">ID Partido Obra</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Partido</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Área</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Expediente</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Objeto Exp</th>
							{showRendicion && (
								<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Exp. Rendición</th>
							)}
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Tipo Pago</th>
							<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">%</th>
							<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Monto</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Fecha Pago</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Nro Inst.</th>
							<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">Monto Habitat</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">Fecha Habitat</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">Instr Habitat</th>
							<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Estado Verif.</th>
						</tr>
					</thead>
					<tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
						{data.rows.map((r) => {
							const idObra = r.id_partido_obra_real || r.id_partido_obra;
							const editadoPorOtro = isEditingByOtherUser(idObra);
							return (
							<tr
								key={r.id}
								className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-150 ${editadoPorOtro ? 'bg-red-200 dark:bg-red-900/30' : ''}`}
								title={editadoPorOtro ? 'Este registro está siendo editado por otro usuario' : ''}
								onDoubleClick={() => handleRowDoubleClick(idObra)}
							>
							<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.id_partido_obra}</td>
							<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.partido || '-'}</td>
							<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{fmtArea(r.area)}</td>
<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.expediente || '-'}</td>
									<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{fmtTipoObjeto(r.tipo_objeto)}</td>
									{showRendicion && (
										<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.exp_rendicion || '-'}</td>
									)}
								<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.tipo_pago}</td>
								<td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{fmtPct(r.porc_pago)}</td>
								<td className="px-4 py-2 text-right text-gray-800 dark:text-gray-200">{fmtMonto(r.monto)}</td>
								<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{fmtFecha(r.fecha_pago)}</td>
								<td className="px-4 py-2 text-gray-700 dark:text-gray-300">{r.nro_instruccion || '-'}</td>
								<td className="px-4 py-2 text-right text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20">{fmtMonto(r.monto_transferido)}</td>
								<td className="px-4 py-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20">{fmtFecha(r.fecha_habitat)}</td>
								<td className="px-4 py-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20">{r.instr_habitat || '-'}</td>
								<td className="px-4 py-2">
									<button
										onClick={(e) => { e.stopPropagation(); abrirVerifModal(r); }}
										title={r.observacion_verif ? r.observacion_verif : 'Cargar verificación'}
										className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${estadoVerifCls(r.estado_verif)}`}
									>
										{estadoVerifLabel(r.estado_verif)}
									</button>
								</td>
							</tr>
							);
						})}
						{!loading && data.rows.length === 0 && (
							<tr>
								<td colSpan={showRendicion ? 15 : 14} className="px-4 py-8 text-center text-gray-400">
									No hay pagos que coincidan con los filtros.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{/* Paginación */}
			{totalPages > 1 && (
				<div className="mt-4 flex items-center justify-center gap-2">
					<button
						onClick={() => navegarPagina(data.page - 1)}
						disabled={data.page <= 1 || loading}
						className="px-3 py-1 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
					>
						Anterior
					</button>
					<span className="text-sm text-gray-500">
						Página {data.page} de {totalPages}
					</span>
					<button
						onClick={() => navegarPagina(data.page + 1)}
						disabled={data.page >= totalPages || loading}
						className="px-3 py-1 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
					>
						Siguiente
					</button>
				</div>
			)}

			{/* Modal de verificación de pago */}
			{verifModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={cerrarVerifModal}>
					<div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
						<h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
							Verificación de pago
						</h3>
						<p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
							Pago <strong className="text-gray-800 dark:text-gray-100">#{verifModal.id}</strong> — {verifModal.expediente || 'sin expediente'}
						</p>

						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
							Estado de verificación
						</label>
						<div className="mb-4 flex gap-2">
							{ESTADOS_VERIF.map((e) => (
								<button
									key={e.value}
									type="button"
									onClick={() => setVerifEstado(e.value)}
									className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
										verifEstado === e.value
											? 'border-red-600 bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300'
											: 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
									}`}
								>
									{e.label}
								</button>
							))}
						</div>

						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
							Observación
						</label>
						<textarea
							value={verifObservacion}
							onChange={(e) => setVerifObservacion(e.target.value)}
							rows={3}
							placeholder="Observaciones sobre la verificación de este pago..."
							className="mb-4 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
						/>

						<div className="flex justify-end gap-3">
							<button
								onClick={cerrarVerifModal}
								disabled={verifGuardando}
								className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-sm disabled:opacity-40"
							>
								Cancelar
							</button>
							<button
								onClick={guardarVerif}
								disabled={verifGuardando}
								className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm disabled:opacity-40"
							>
								{verifGuardando ? 'Guardando...' : 'Guardar'}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
